import { GrowthGoal } from "@/types/user";
import { goalsService } from "@/services/goalsService";

/**
 * Fetch growth goals for a specific user
 */
export const fetchGrowthGoals = async (
  userId: string
): Promise<GrowthGoal[]> => {
  try {
    console.log("Fetching growth goals for user:", userId);
    const response = await goalsService.getGoals();

    // API returns { goals: [...], total, limit, offset } or array directly
    const goalsArray = Array.isArray(response) ? response : (response?.goals || []);

    // Transform API response to GrowthGoal format
    return goalsArray.map((goal: any) => ({
      id: goal.id,
      userId: goal.userId,
      title: goal.title,
      description: goal.description || "",
      progress: goal.progress,
      category: goal.category,
      dueDate: goal.dueDate,
      createdAt: goal.createdAt,
      updatedAt: goal.updatedAt,
      status: goal.status || 'active',
      priority: goal.priority || 'medium',
      ai_suggested: goal.aiSuggested || false,
    }));
  } catch (error) {
    console.error("Failed to fetch growth goals", error);
    return [];
  }
};

/**
 * Create a new growth goal
 */
export const createGrowthGoal = async (
  userId: string,
  goal: Omit<GrowthGoal, "id" | "createdAt" | "updatedAt">
): Promise<GrowthGoal | null> => {
  try {
    const data = await goalsService.createGoal({
      title: goal.title,
      description: goal.description,
      category: goal.category,
      dueDate: goal.dueDate,
      progress: goal.progress,
    });

    if (!data) {
      console.error("Error creating growth goal: no data returned");
      return null;
    }

    return {
      id: data.id,
      userId: data.userId,
      title: data.title,
      description: data.description || "",
      progress: data.progress,
      category: data.category,
      dueDate: data.dueDate,
      status: data.status || 'active',
      priority: data.priority || 'medium',
      ai_suggested: data.aiSuggested || false,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  } catch (error) {
    console.error("Failed to create growth goal", error);
    return null;
  }
};

/**
 * Update a growth goal
 */
export const updateGrowthGoal = async (
  goalId: string,
  updates: Partial<GrowthGoal>
): Promise<GrowthGoal | null> => {
  try {
    const data = await goalsService.updateGoal(goalId, updates);

    if (!data) {
      console.error("Error updating growth goal: no data returned");
      return null;
    }

    return {
      id: data.id,
      userId: data.userId,
      title: data.title,
      description: data.description || "",
      progress: data.progress,
      category: data.category,
      dueDate: data.dueDate,
      status: data.status || 'active',
      priority: data.priority || 'medium',
      ai_suggested: data.aiSuggested || false,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    };
  } catch (error) {
    console.error("Failed to update growth goal", error);
    return null;
  }
};

/**
 * Delete a growth goal
 */
export const deleteGrowthGoal = async (
  goalId: string
): Promise<boolean> => {
  try {
    await goalsService.deleteGoal(goalId);
    return true;
  } catch (error) {
    console.error("Failed to delete growth goal", error);
    return false;
  }
};
